import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import axios from "api";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userLogin } from "store/reducers/auth";
import { RootState } from "store";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import dayjs, { Dayjs } from "dayjs";
import { formatMoney } from "utils/money";
import {
  waitForSubscriptionPaymentStatus,
  SUBSCRIPTION_PAYMENT_PENDING_MESSAGE,
} from "utils/waitForSubscriptionPaymentStatus";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableMeta,
  MUIDataTableOptions,
} from "mui-datatables";

// ── Theme: matches ExpiringSubscriptions exactly ──────────────────────────────
const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatPrice = (amount: number, currencyCode: string) => {
  if (Number(amount) === 0) return "Free";
  return formatMoney(Number(amount), currencyCode || "USD");
};

const loadRazorpayScript = (): Promise<void> => {
  if (typeof window !== "undefined" && (window as any).Razorpay)
    return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement("script"); // NOSONAR - third-party hosted script (no stable SRI hash published)
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });
};

const formatDuration = (days: number) => {
  if (days >= 365) return `${Math.round(days / 365)} year(s)`;
  if (days >= 30) return `${Math.round(days / 30)} month(s)`;
  return `${days} day(s)`;
};

const InvoiceDownloadButton = ({
  invoiceId,
  onDownload,
}: {
  invoiceId?: number | null;
  onDownload: (invoiceId?: number | null) => void;
}) => (
  <Button
    size="small"
    variant="outlined"
    disabled={!invoiceId}
    onClick={() => onDownload(invoiceId)}
  >
    Download
  </Button>
);

const getEndDateDisplay = (
  formattedValue: string,
  hasSelectedType: boolean
) => {
  if (formattedValue) return formattedValue;
  return hasSelectedType ? "Select a valid start date" : "Select a plan";
};

const getEndDateHelperText = (
  dateValue: Dayjs | null,
  hasSelectedType: boolean
) => {
  if (dateValue) return `Runs until ${dateValue.format("MMM D, YYYY")}.`;
  return hasSelectedType
    ? "Select a valid start date to see the end date."
    : "Choose a plan to calculate the end date.";
};

const getStartDateHelperText = (
  desiredStartDate: string,
  parsedStart: Dayjs,
  durationDays: number
) => {
  if (!desiredStartDate) {
    return "Billing dates are calculated from the selected start date.";
  }
  if (!parsedStart.isValid()) {
    return "Enter a valid start date.";
  }
  const projectedEnd = parsedStart.add(durationDays - 1, "day");
  return `Covers ${parsedStart.format("MMM D, YYYY")} – ${projectedEnd.format(
    "MMM D, YYYY"
  )}.`;
};

// ── Table options: full toolbar + pagination ──────────────────────────────────
const historyTableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
  rowsPerPage: 10,
  rowsPerPageOptions: [5, 10, 25],
};

// ── Types ─────────────────────────────────────────────────────────────────────
interface BillingSubscriptionType {
  id: number;
  name: string;
  description: string | null;
  duration_days: number;
  price: string | number;
  is_active: boolean;
  display_price?: number;
  display_currency?: string;
}

interface ClientAgreementSummary {
  plan_name: string;
  start_date: string;
  end_date: string;
  agreed_price: number;
}

interface PaymentHistoryRow {
  id: number;
  order_id?: string | null;
  payment_id?: string | null;
  created_at?: string;
  plan_name?: string | null;
  amount?: number | string | null;
  currency_code?: string | null;
  invoice_id?: number | null;
}

interface FutureConfirmDetails {
  existing: { start_date: string; end_date: string };
  proposed: { start_date: string; end_date: string };
}

const useSubscriptionTypes = () => {
  const [subscriptionTypes, setSubscriptionTypes] = useState<
    BillingSubscriptionType[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get("/billing/subscription-types");
        setSubscriptionTypes(res.data.subscriptionTypes ?? []);
      } catch {
        setSubscriptionTypes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTypes();
  }, []);

  return { subscriptionTypes, loading };
};

const useCurrentAgreement = (clientId?: number | null) => {
  const [currentAgreement, setCurrentAgreement] =
    useState<ClientAgreementSummary | null>(null);
  const [loadingAgreement, setLoadingAgreement] = useState(true);

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      if (!clientId) {
        setCurrentAgreement(null);
        setLoadingAgreement(false);
        return;
      }
      setLoadingAgreement(true);
      try {
        const res = await axios.get(`/billing/clients/${clientId}`);
        const agreements = res.data?.agreements ?? [];
        const today = dayjs().startOf("day");
        const activeAgreement =
          agreements.find((agreement: any) => {
            const start = agreement?.start_date
              ? dayjs(agreement.start_date).startOf("day")
              : null;
            const end = agreement?.end_date
              ? dayjs(agreement.end_date).startOf("day")
              : null;
            if (!start || !end) return false;
            return (
              (start.isSame(today) || start.isBefore(today)) &&
              (end.isSame(today) || end.isAfter(today))
            );
          }) ?? null;
        setCurrentAgreement(
          activeAgreement || (agreements.length > 0 ? agreements[0] : null)
        );
      } catch {
        setCurrentAgreement(null);
      } finally {
        setLoadingAgreement(false);
      }
    };
    fetchCurrentPlan();
  }, [clientId]);

  return { currentAgreement, loadingAgreement };
};

const usePaymentHistory = (clientId?: number | null) => {
  const [history, setHistory] = useState<PaymentHistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      if (!clientId) {
        setHistory([]);
        setHistoryLoading(false);
        return;
      }
      setHistoryLoading(true);
      try {
        const res = await axios.get("/payments/history");
        setHistory(res.data?.history ?? []);
      } catch {
        setHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [clientId]);

  return { history, historyLoading };
};

// ── Page ──────────────────────────────────────────────────────────────────────
export default function VendorSubscriptionPage() {
  const { fetchSuccess, fetchError } = useSnackbar();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const client_id = user?.clientId ?? (user as { client_id?: number })?.client_id;
  const clientCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const { subscriptionTypes, loading: subscriptionTypesLoading } =
    useSubscriptionTypes();
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedType, setSelectedType] =
    useState<BillingSubscriptionType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { currentAgreement, loadingAgreement } = useCurrentAgreement(client_id);
  const { history, historyLoading } = usePaymentHistory(client_id);
  const [desiredStartDate, setDesiredStartDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [startDateError, setStartDateError] = useState<string | null>(null);
  const [futureConfirmDetails, setFutureConfirmDetails] =
    useState<FutureConfirmDetails | null>(null);
  const [forceFuture, setForceFuture] = useState(false);
  const parsedDesiredStart = useMemo(
    () => dayjs(desiredStartDate, "YYYY-MM-DD", true),
    [desiredStartDate]
  );
  const derivedEndDate = useMemo(() => {
    if (!selectedType || !parsedDesiredStart.isValid()) return null;
    return parsedDesiredStart.add(selectedType.duration_days - 1, "day");
  }, [selectedType, parsedDesiredStart]);
  const derivedEndDateValue = derivedEndDate
    ? derivedEndDate.format("YYYY-MM-DD")
    : "";
  const hasSelectedType = Boolean(selectedType);
  const planEndDateValue = getEndDateDisplay(
    derivedEndDateValue,
    hasSelectedType
  );
  const dialogEndDatePlaceholder = hasSelectedType
    ? "Select a valid start date"
    : "Select a plan";
  const dialogEndDateHelper = getEndDateHelperText(
    derivedEndDate,
    hasSelectedType
  );
  let dialogStartHelperText =
    "Billing dates are calculated from the selected start date.";
  if (hasSelectedType && selectedType) {
    dialogStartHelperText = getStartDateHelperText(
      desiredStartDate,
      parsedDesiredStart,
      selectedType.duration_days
    );
  }
  if (startDateError) {
    dialogStartHelperText = startDateError;
  }

  const computeDefaultStartDate = useCallback(() => {
    if (currentAgreement?.end_date) {
      const next = dayjs(currentAgreement.end_date);
      if (next.isValid()) {
        return next.add(1, "day").format("YYYY-MM-DD");
      }
    }
    return dayjs().format("YYYY-MM-DD");
  }, [currentAgreement]);

  useEffect(() => {
    if (!openConfirm) {
      setDesiredStartDate(computeDefaultStartDate());
      setStartDateError(null);
      setForceFuture(false);
    }
  }, [computeDefaultStartDate, openConfirm]);

  useEffect(() => {
    const nextDefault = computeDefaultStartDate();
    setDesiredStartDate((prev) => {
      if (!prev) return nextDefault;
      const parsed = dayjs(prev, "YYYY-MM-DD", true);
      if (!parsed.isValid()) return nextDefault;
      if (parsed.isBefore(dayjs().startOf("day"))) return nextDefault;
      return prev;
    });
  }, [computeDefaultStartDate]);

  // data fetching handled via custom hooks

  // ── Invoice download ─────────────────────────────────────────────────────
  const handleDownloadInvoice = useCallback(
    async (invoiceId?: number | null) => {
      if (!invoiceId) {
        fetchError("Invoice not available for this transaction.");
        return;
      }
      try {
        const res = await axios.get(`/billing/invoices/${invoiceId}/download`, {
          responseType: "blob",
        });
        const rawContentType = res.headers["content-type"];
        const contentType =
          typeof rawContentType === "string"
            ? rawContentType
            : Array.isArray(rawContentType) && typeof rawContentType[0] === "string"
              ? rawContentType[0]
              : "application/pdf";
        const dispositionHeader = res.headers["content-disposition"];
        const blob = new Blob([res.data], { type: contentType });

        let downloadName = `invoice-${invoiceId}.pdf`;
        if (dispositionHeader) {
          const match = dispositionHeader.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
          if (match?.[1]) {
            const rawName = match[1].replace(/"/g, "");
            try {
              downloadName = decodeURIComponent(rawName);
            } catch {
              downloadName = rawName;
            }
          }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (err: any) {
        fetchError(err?.response?.data?.error || "Could not download invoice.");
      }
    },
    [fetchError]
  );

  // ── Table columns ────────────────────────────────────────────────────────
  const historyColumns: MUIDataTableColumn[] = useMemo(
    () => [
      {
        name: "order_id",
        label: "Order ID",
      },
      {
        name: "payment_id",
        label: "Payment ID",
      },
      {
        name: "created_at",
        label: "Payment Date",
        options: {
          customBodyRender: (value: string) =>
            value ? dayjs(value).format("MMM D, YYYY HH:mm") : "—",
        },
      },
      {
        name: "plan_name",
        label: "Subscription",
      },
      {
        name: "amount",
        label: "Amount",
        options: {
          customBodyRender: (value: number, tableMeta: MUIDataTableMeta) => {
            return formatPrice(Number(value ?? 0), String(clientCurrency || "USD"));
          },
        },
      },
      {
        name: "currency_code",
        label: "Currency",
        options: {
          display: false,
          filter: false,
          sort: false,
        },
      },
      {
        name: "invoice_id",
        label: "Invoice",
        options: {
          customBodyRender: (value: number) => (
            <InvoiceDownloadButton
              invoiceId={value}
              onDownload={handleDownloadInvoice}
            />
          ),
        },
      },
    ],
    [clientCurrency, handleDownloadInvoice]
  );

  // ── Razorpay payment ─────────────────────────────────────────────────────
  const handleSelectType = (st: BillingSubscriptionType) => {
    setSelectedType(st);
    setDesiredStartDate(computeDefaultStartDate());
    setStartDateError(null);
    setFutureConfirmDetails(null);
    setForceFuture(false);
    setOpenConfirm(true);
  };

  const handlePayWithRazorpay = async (overrideForce = false) => {
    if (!selectedType || !client_id) {
      fetchError("Session error. Please sign in again.");
      return;
    }
    if (!desiredStartDate) {
      setStartDateError("Please choose a start date.");
      return;
    }
    const parsedStart = parsedDesiredStart;
    if (!parsedStart.isValid()) {
      setStartDateError("Invalid start date format.");
      return;
    }
    if (parsedStart.isBefore(dayjs().startOf("day"))) {
      setStartDateError("Start date cannot be in the past.");
      return;
    }
    setStartDateError(null);
    setSubmitting(true);
    try {
      console.log("[PAYMENT_DEBUG] vendor: create-order request");
      const forceFlag = overrideForce || forceFuture;
      const createRes = await axios.post<{
        orderId: string;
        amount: number;
        currency: string;
        key_id: string;
        subscription_id?: number;
        start_date?: string;
      }>("/payments/create-order", {
        subscriptionId: selectedType.id,
        vendorId: client_id,
        start_date: parsedStart.format("YYYY-MM-DD"),
        force: forceFlag,
      });
      const { orderId, amount, currency, key_id, start_date } = createRes.data;
      console.log("[PAYMENT_DEBUG] vendor: create-order OK, opening Razorpay", {
        orderId,
      });
      const normalizedStartDate =
        start_date ?? parsedStart.format("YYYY-MM-DD");
      const forceFlagUsed = forceFlag;

      await loadRazorpayScript();
      const Razorpay = (window as any).Razorpay;
      if (!Razorpay) {
        fetchError("Payment gateway could not be loaded.");
        setSubmitting(false);
        return;
      }

      const rzp = Razorpay({
        key: key_id,
        amount: Number(amount),
        currency: currency || String(clientCurrency || "USD"),
        order_id: orderId,
        name: "Scout Track Solutions",
        description: `${selectedType.name} subscription`,
        handler: async (response: {
          razorpay_payment_id: string;
          razorpay_order_id: string;
          razorpay_signature: string;
        }) => {
          console.log("[PAYMENT_DEBUG] vendor: Razorpay handler (payment success UI)", {
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
          });
          try {
            const verifyPayload = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              subscription_id: selectedType?.id,
              start_date: normalizedStartDate,
              force: forceFlagUsed,
            };
            const verifyPromise = axios.post("/payments/verify", verifyPayload);
            const poll = await waitForSubscriptionPaymentStatus(
              response.razorpay_order_id,
              "vendor"
            );
            let verifyRes: Awaited<typeof verifyPromise> | null = null;
            try {
              verifyRes = await verifyPromise;
            } catch (e) {
              console.log("[PAYMENT_DEBUG] vendor: /payments/verify request failed (tab closed or network)", {
                orderId: response.razorpay_order_id,
                message: (e as Error)?.message,
              });
              verifyRes = null;
            }
            const verifyOk = !!(verifyRes && (verifyRes.status === 200 || verifyRes.status === 201));

            if (poll.activated || verifyOk) {
              const scheduledStart =
                verifyRes?.data?.startDate ??
                (poll.activated ? normalizedStartDate : undefined);
              const successMessage =
                scheduledStart && dayjs(scheduledStart).isAfter(dayjs(), "day")
                  ? `Payment successful. Your subscription is scheduled from ${dayjs(
                      scheduledStart
                    ).format("MMM D, YYYY")}.`
                  : "Payment successful. Your subscription is now active.";
              fetchSuccess(successMessage);
              setOpenConfirm(false);
              setSelectedType(null);
              setForceFuture(false);
              setFutureConfirmDetails(null);
              const authRes = await axios.get("/auth");
              dispatch(userLogin({ user: authRes.data }));
              navigate("/dashboard");
            } else {
              
              fetchSuccess(SUBSCRIPTION_PAYMENT_PENDING_MESSAGE);
              setOpenConfirm(false);
              setSelectedType(null);
              setForceFuture(false);
              setFutureConfirmDetails(null);
              const authRes = await axios.get("/auth");
              dispatch(userLogin({ user: authRes.data }));
              navigate("/dashboard");
            }
          } catch (err: any) {
            fetchError(
              err.response?.data?.error || "Payment verification failed."
            );
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
          },
        },
      });
      rzp.open();
    } catch (err: any) {
      const code = err.response?.data?.code;
      if (code === "FUTURE_SUBSCRIPTION_CONFIRM") {
        setFutureConfirmDetails(err.response?.data?.details || null);
        setSubmitting(false);
        return;
      }
      fetchError(
        err.response?.data?.error ||
          "Could not start payment. Please try again."
      );
      setSubmitting(false);
    }
  };

  // ── Theming helpers ──────────────────────────────────────────────────────
  const isDark = theme.palette.mode === "dark";
  const cardBg = isDark
    ? alpha(theme.palette.background.paper, 0.5)
    : alpha(theme.palette.grey[800], 0.08);
  const textOnDark = isDark
    ? theme.palette.text.primary
    : theme.palette.grey[900];
  const textMuted = isDark
    ? theme.palette.text.secondary
    : theme.palette.grey[600];

  // ── Render ───────────────────────────────────────────────────────────────
  return (
      <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        {/* ── Plan picker ── */}
        <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>
          Choose a plan
        </Typography>
        <Box
          sx={{
            mb: 3,
            p: 2.5,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            background: alpha(
              theme.palette.mode === "dark"
                ? theme.palette.background.default
                : theme.palette.grey[50],
              0.9
            ),
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Plan scheduling
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick the date you want the next subscription to start. If it overlaps
            with an existing plan you&apos;ll be asked to adjust. Selecting a date
            after the current plan ends will schedule the new plan automatically.
          </Typography>
          {currentAgreement && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Current plan:{" "}
              <strong>
                {dayjs(currentAgreement.start_date).format("MMM D, YYYY")} –{" "}
                {dayjs(currentAgreement.end_date).format("MMM D, YYYY")}
              </strong>
            </Typography>
          )}
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                Start Date
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={desiredStartDate}
                onChange={(event) => {
                  setDesiredStartDate(event.target.value);
                  setStartDateError(null);
                  setFutureConfirmDetails(null);
                  setForceFuture(false);
                }}
                InputLabelProps={{ shrink: false }}
                inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
                error={Boolean(startDateError)}
                helperText={
                  startDateError ||
                  "Choose when this plan should begin."
                }
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                End Date
              </Typography>
              <TextField
                fullWidth
                value={planEndDateValue}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: false }}
                sx={{ mt: 0.5 }}
                helperText={
                  derivedEndDate
                    ? `Runs until ${derivedEndDate.format("MMM D, YYYY")}.`
                    : ""
                }
              />
            </Box>
          </Stack>
        </Box>
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            background: isDark
              ? alpha(theme.palette.background.default, 0.6)
              : theme.palette.grey[100],
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2.5,
              overflowX: "auto",
              pb: 1.5,
              minHeight: 260,
              alignItems: "stretch",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 4,
                bgcolor: "action.hover",
              },
            }}
          >
            {/* Free tier card */}
            <Card
              elevation={0}
              sx={{
                flex: "0 0 280px",
                borderRadius: 3,
                background: cardBg,
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                color: textOnDark,
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
              }}
            >
              <CardContent
                sx={{
                  py: 2.5,
                  px: 2.5,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  "&:last-child": { pb: 2.5 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 22, color: textMuted }} />
                  <Typography variant="body2" fontWeight={500}>
                    Up to 5 customers
                  </Typography>
                </Box>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ color: textOnDark, mt: 1.5 }}
                >
                  Free
                </Typography>
                <Typography variant="body2" sx={{ color: textMuted, mt: 0.5 }}>
                  See what you can do
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.75rem",
                    fontWeight: 700,
                    color: textOnDark,
                    mt: 2,
                  }}
                >
                  Free
                </Typography>
                <Typography variant="caption" sx={{ color: textMuted }}>
                  / month
                </Typography>
                <Box sx={{ flex: 1, minHeight: 16 }} />
                <Button
                  fullWidth
                  variant="outlined"
                  disabled
                  sx={{
                    mt: 2,
                    py: 1.25,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.action.disabled, 0.08),
                    borderColor: "divider",
                    color: textMuted,
                  }}
                >
                  {!currentAgreement
                    ? "Current plan (free tier)"
                    : "Included in free tier"}
                </Button>
                {!currentAgreement && !loadingAgreement && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    Active indefinitely until you upgrade.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Paid plan cards */}
            {subscriptionTypesLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", px: 4 }}>
                <Typography color="text.secondary">Loading plans...</Typography>
              </Box>
            ) : (
              subscriptionTypes.map((st, index) => {
                const isHighlighted = index === 0;
                const isCurrentPlan =
                  !!currentAgreement &&
                  currentAgreement.plan_name === st.name;
                return (
                  <Card
                    key={st.id}
                    elevation={0}
                    sx={{
                      flex: "0 0 280px",
                      borderRadius: 3,
                      background: isHighlighted
                        ? alpha(theme.palette.primary.main, 0.06)
                        : cardBg,
                      border: `2px solid ${
                        isHighlighted
                          ? theme.palette.primary.main
                          : alpha(theme.palette.primary.main, 0.3)
                      }`,
                      color: textOnDark,
                      display: "flex",
                      flexDirection: "column",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: isHighlighted ? 8 : 4,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        py: 2.5,
                        px: 2.5,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        "&:last-child": { pb: 2.5 },
                      }}
                    >
                      {isHighlighted && (
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          sx={{
                            color: theme.palette.primary.main,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Popular
                        </Typography>
                      )}
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: textOnDark, mt: isHighlighted ? 0.5 : 1.5 }}
                      >
                        {st.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: textMuted, minHeight: 40, mt: 0.5 }}
                      >
                        {st.description || formatDuration(st.duration_days)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "1.75rem",
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                          mt: 1.5,
                        }}
                      >
                        {formatPrice(
                          Number(st.display_price ?? st.price),
                          String(clientCurrency || "USD")
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: textMuted }}>
                        for {formatDuration(st.duration_days)}
                      </Typography>
                      <Box sx={{ flex: 1, minHeight: 16 }} />
                      {isCurrentPlan && currentAgreement ? (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            Active{" "}
                            {dayjs(currentAgreement.start_date).format("MMM D")}{" "}
                            –{" "}
                            {dayjs(currentAgreement.end_date).format(
                              "MMM D, YYYY"
                            )}
                          </Typography>
                          <Button
                            fullWidth
                            variant="outlined"
                            disabled
                            sx={{
                              mt: 2,
                              py: 1.25,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              borderColor: theme.palette.success.main,
                              color: theme.palette.success.main,
                            }}
                          >
                            Current plan
                          </Button>
                        </>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => handleSelectType(st)}
                          sx={{
                            mt: 2,
                            py: 1.25,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            boxShadow: isHighlighted ? 2 : 0,
                          }}
                        >
                          Upgrade to {st.name}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </Box>
        </Box>

        {/* ── Confirm / Pay dialog ── */}
        <Dialog
          open={openConfirm}
          onClose={() => !submitting && setOpenConfirm(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {selectedType ? (
              <>
                {selectedType.name}{" "}
                <Typography
                  component="span"
                  color="primary.main"
                  fontWeight={700}
                  sx={{ ml: 1 }}
                >
                  {formatPrice(
                    Number(selectedType.display_price ?? selectedType.price),
                    String(clientCurrency || "USD")
                  )}
                </Typography>
              </>
            ) : (
              "Confirm plan"
            )}
          </DialogTitle>
          <DialogContent>
            {selectedType && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {formatDuration(selectedType.duration_days)}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Choose when this plan should start. It will run for{" "}
                  {formatDuration(selectedType.duration_days)} from that date.
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    mt: 2,
                  }}
                >
                  <Box>
                    <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                      Start Date
                    </Typography>
                    <TextField
                      fullWidth
                      type="date"
                      value={desiredStartDate}
                      onChange={(event) => {
                        setDesiredStartDate(event.target.value);
                        setStartDateError(null);
                      }}
                      inputProps={{ min: dayjs().format("YYYY-MM-DD") }}
                      InputLabelProps={{ shrink: false }}
                      error={Boolean(startDateError)}
                      helperText={dialogStartHelperText}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
                      End Date
                    </Typography>
                    <TextField
                      fullWidth
                      type="date"
                      value={derivedEndDateValue || ""}
                      placeholder={dialogEndDatePlaceholder}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: false }}
                      helperText={dialogEndDateHelper}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap", gap: 1 }}>
            <Button
              onClick={() => setOpenConfirm(false)}
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </Button>
            {selectedType && Number(selectedType.price) > 0 && (
              <Button
                variant="contained"
                onClick={() => handlePayWithRazorpay()}
                disabled={submitting}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {submitting ? "Opening..." : "Pay with Razorpay"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(futureConfirmDetails)}
          onClose={() => {
            if (!submitting) {
              setFutureConfirmDetails(null);
              setForceFuture(false);
            }
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Schedule After Current Plan?
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {futureConfirmDetails?.existing
                ? `You already have a subscription running from ${dayjs(
                    futureConfirmDetails.existing.start_date
                  ).format("MMM D, YYYY")} to ${dayjs(
                    futureConfirmDetails.existing.end_date
                  ).format("MMM D, YYYY")}.`
                : ""}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Start this plan on{" "}
              {futureConfirmDetails?.proposed
                ? dayjs(
                    futureConfirmDetails.proposed.start_date
                  ).format("MMM D, YYYY")
                : ""}
              ? It will activate automatically after your current plan ends.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setFutureConfirmDetails(null);
                setForceFuture(false);
              }}
              color="secondary"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setFutureConfirmDetails(null);
                setForceFuture(true);
                handlePayWithRazorpay(true);
              }}
              disabled={submitting}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Yes, continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Transaction history — styled like ExpiringSubscriptions ── */}
        <Card style={{ padding: "24px", margin: "40px auto" }}>
          <SoftBox
            sx={{
              "& .MuiTableRow-root:not(:last-child)": {
                "& td": (theme: Theme) => ({
                  borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
                }),
              },
            }}
            className="vendor-transactions-table"
          >
            <ThemeProvider theme={getMuiTheme}>
              <MUIDataTable
                title="Transaction History"
                data={history}
                columns={historyColumns}
                options={{
                  ...historyTableOptions,
                  textLabels: {
                    body: {
                      noMatch: historyLoading
                        ? "Loading..."
                        : "No transactions yet.",
                    },
                  },
                }}
              />
            </ThemeProvider>
          </SoftBox>
        </Card>
      </Box>
    </DashboardLayout>

  );
}
